import { Badge, Card, CardBody, FormLabel, Heading, HStack, Icon, Wrap, WrapItem, PopoverTrigger, Box } from '@chakra-ui/react';
import { CalendarIcon } from '@chakra-ui/icons';
import { RangeDatepicker } from '../calendarPicker';
import { useIntl } from 'react-intl';
import React from 'react';
import { isSameDay, differenceInDays } from 'date-fns';
import { TimeRangeVisualizer } from '../common/DateRangeVisualizer';
import { FilterChip } from '../attendance/AttendanceListFilter';

type IncompleteRecordsFilterProps = {
    onDateChange: (dates: Date[]) => void;
    selectedDates: Date[];
    totalRecords?: number;
    [key: string]: any;
};

const MAX_DATE_RANGE_DAYS = 31; // One month maximum

export const IncompleteRecordsFilter: React.FC<IncompleteRecordsFilterProps> = ({
    onDateChange,
    selectedDates,
    totalRecords,
    ...restCardProps
}) => {
    const intl = useIntl();

    function isChipSelected(chipType: 'day' | 'yesterday' | 'week' | 'month'): boolean {
        let start = new Date();
        let end = new Date();
        switch (chipType) {
            case 'day':
                start.setHours(0, 0, 0, 0);
                end.setHours(23, 59, 59, 999);
                break;
            case 'yesterday':
                start.setDate(start.getDate() - 1);
                start.setHours(0, 0, 0, 0);
                end = new Date(start);
                end.setHours(23, 59, 59, 999);
                break;
            case 'week':
                start.setHours(0, 0, 0, 0);
                start.setDate(start.getDate() - start.getDay());
                end.setHours(23, 59, 59, 999);
                end.setDate(start.getDate() + 6);
                break;
            case 'month':
                start.setDate(1);
                start.setHours(0, 0, 0, 0);
                end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
                end.setHours(23, 59, 59, 999);
                break;
        }

        if (isSameDay(selectedDates[0], start) && isSameDay(selectedDates[1], end)) {
            return true;
        }

        return false;
    }

    function handleDateChange(dates: Date[]) {
        if (dates.length === 2 && differenceInDays(dates[1], dates[0]) > MAX_DATE_RANGE_DAYS) {
            // Limit to one month
            const limitedEnd = new Date(dates[0]);
            limitedEnd.setDate(limitedEnd.getDate() + MAX_DATE_RANGE_DAYS);
            limitedEnd.setHours(23, 59, 59, 999);
            onDateChange([dates[0], limitedEnd]);
        } else {
            onDateChange(dates);
        }
    }

    return (
        <Card {...restCardProps} boxShadow={'md'} borderRadius={'xl'}>
            <CardBody>
                <HStack mb={2} alignItems={'center'} justifyContent={'space-between'}>
                    <Heading alignItems={'center'} size={'md'}>
                        {intl.formatMessage({ id: 'filter' })}
                    </Heading>
                    <Badge borderRadius={'md'} alignItems={'center'} display={'flex'}>
                        {totalRecords ?? 0}
                        <Icon ml={1} as={CalendarIcon} />
                    </Badge>
                </HStack>

                <FormLabel>{intl.formatMessage({ id: 'period' })}</FormLabel>
                <RangeDatepicker
                    selectedDates={selectedDates}
                    onDateChange={(dates) => {
                        handleDateChange(dates);
                    }}
                    usePortal
                    configs={{
                        placeHolders: [intl.formatMessage({ id: 'dateFrom' }), intl.formatMessage({ id: 'dateTo' })],
                    }}
                >
                    {(dates) => (
                        <PopoverTrigger>
                            <Box>
                                <TimeRangeVisualizer
                                    interactive
                                    cursor={'pointer'}
                                    dateFrom={dates[0] ? intl.formatDate(dates[0]) : intl.formatMessage({ id: 'dateFrom' })}
                                    dateTo={dates[1] ? intl.formatDate(dates[1]) : intl.formatMessage({ id: 'dateTo' })}
                                />
                            </Box>
                        </PopoverTrigger>
                    )}
                </RangeDatepicker>
                <Wrap mt={2}>
                    <WrapItem>
                        <FilterChip
                            colorScheme="gray"
                            label={intl.formatMessage({ id: 'timefilter.today' })}
                            onClear={() => {
                                onDateChange([]);
                            }}
                            selected={isChipSelected('day')}
                            onSelect={() => {
                                const start = new Date();
                                const end = new Date();
                                end.setHours(23, 59, 59, 999);
                                start.setHours(0, 0, 0, 0);
                                onDateChange([start, end]);
                            }}
                        />
                    </WrapItem>
                    <WrapItem>
                        <FilterChip
                            colorScheme="gray"
                            label={intl.formatMessage({ id: 'timefilter.yesterday' })}
                            onClear={() => {
                                onDateChange([]);
                            }}
                            selected={isChipSelected('yesterday')}
                            onSelect={() => {
                                const start = new Date();
                                start.setDate(start.getDate() - 1);
                                start.setHours(0, 0, 0, 0);
                                const end = new Date(start);
                                end.setHours(23, 59, 59, 999);
                                onDateChange([start, end]);
                            }}
                        />
                    </WrapItem>
                    <WrapItem>
                        <FilterChip
                            colorScheme="gray"
                            label={intl.formatMessage({ id: 'timefilter.week' })}
                            onClear={() => {
                                onDateChange([]);
                            }}
                            selected={isChipSelected('week')}
                            onSelect={() => {
                                const start = new Date();
                                start.setHours(0, 0, 0, 0);
                                start.setDate(start.getDate() - start.getDay());
                                const end = new Date();
                                end.setHours(23, 59, 59, 999);
                                end.setDate(start.getDate() + 6);
                                onDateChange([start, end]);
                            }}
                        />
                    </WrapItem>
                    <WrapItem>
                        <FilterChip
                            colorScheme="gray"
                            label={intl.formatMessage({ id: 'timefilter.month' })}
                            onClear={() => {
                                onDateChange([]);
                            }}
                            selected={isChipSelected('month')}
                            onSelect={() => {
                                const start = new Date();
                                start.setDate(1);
                                start.setHours(0, 0, 0, 0);
                                const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
                                end.setHours(23, 59, 59, 999);
                                onDateChange([start, end]);
                            }}
                        />
                    </WrapItem>
                </Wrap>
            </CardBody>
        </Card>
    );
};
